// The shared axios instance resolves failed requests with the AxiosError instead of
// rejecting (see axios-base.ts), so an upload "response" may be either an
// AxiosResponse or an AxiosError. Both shapes are read here so the toast can name
// the reason (HTTP status or network error code) instead of a bare "unexpected error".

interface FailedUploadLike {
    status?: unknown;
    code?: unknown;
    response?: { status?: unknown } | null;
}

const describeFailure = (response: unknown): string | null => {
    const candidate = response as FailedUploadLike | null | undefined;
    const status = candidate?.response?.status ?? candidate?.status;
    if (typeof status === 'number') {
        return `HTTP ${status}`;
    }
    if (typeof candidate?.code === 'string' && candidate.code.length > 0) {
        return candidate.code;
    }
    return null;
};

/**
 * Returns the user-facing message when at least one upload did not come back
 * with HTTP 200, or null when every upload succeeded.
 */
export const getFailedUploadsMessage = (responses: unknown[]): string | null => {
    const failed = responses.filter(
        (response) => (response as FailedUploadLike | null | undefined)?.status !== 200
    );
    if (failed.length === 0) {
        return null;
    }
    const reasons = Array.from(
        new Set(failed.map(describeFailure).filter((reason): reason is string => reason !== null))
    );
    const details = reasons.length > 0 ? ` (${reasons.join(', ')})` : '';
    return `Some files are not uploaded due to an unexpected error${details}.`;
};
